import { capitalize, type ComputedRef, type InjectionKey, type Ref } from "vue";
import moment from "moment";
import type { Moment } from "moment";
import {
  MetricPeriodType,
  type IncomeStatementAccountSpecificationsQuery,
  type RawBalanceAccountSpecificationsQuery,
  type IncomeStatementAccountSpecificationsPortfolioQuery,
  type RawBalanceAccountSpecificationsPortfolioQuery,
  type BalancePropertyQuery,
  type BalancePortfolioQuery,
  type ResultsPropertyQuery,
  type ResultsPortfolioQuery,
} from "~/graphql/generated/graphql";
import { type TranslationKey, filterUniqueOn, flattenTree, getSet, groupBy, pushGet, sum } from "~/helpers/common-helpers";
import type { NamespacePortfolioPropertyBalance, DateRangeConfig } from "~/helpers/user-preferences-helpers";
import type { Currency } from "~/helpers/apollo/types";
import { hasValue } from "~/helpers/common-helpers";
import type { BudgetRecord, BudgetRecordsEntity, Category, Property } from "~/helpers/entities/property-helpers";
import { getPeriodBudgetsIntersect, type AccountBudgetCell, type BudgetCell, type CategoryBudgetCell } from "~/helpers/budgets-helpers";
import type { Account } from "~/helpers/entities/account-helpers";
import type { BenchmarkGroupOption } from "~/helpers/modules/opex-helpers";

export type FinancialStatement = {
  id: string;
  explanation?: Nullable<string>;
  entryDate?: Nullable<string>;
  actualCostWithVat?: Nullable<number>;
  currency: string;
};

export const fitDate = (_date: Moment, periodType: MetricPeriodType) => {
  const date = moment.utc(_date);
  switch (periodType) {
    case "MONTHLY":
      return date.startOf("month");
    case "QUARTERLY":
      return date.startOf("quarter");
    case "YEARLY":
      return date.startOf("year");
    default:
      return date;
  }
};

export const fitDateString = (dateStr: string, periodType: MetricPeriodType) => fitDate(moment.utc(dateStr), periodType);

// Small utility pure functions for improved clarity and reusability
export const findCategoryById = <T extends { id: string }>(categories: T[], id: string): T | undefined => categories.find((c) => c.id === id);

export const findBudgetValue = (budgetValues: any[], budgetId: string) => budgetValues.find((bv) => bv.budgetId === budgetId);

export const sumNumbers = (numbers: number[]): number => numbers.reduce((sum, num) => sum + (num || 0), 0);

export const createEmptyBudgetValues = (): any[] => [];

export const getPeriodHeader = (period: Moment, periodType: MetricPeriodType | null) => {
  switch (periodType) {
    case "MONTHLY":
      return capitalize(period.format("MMM YYYY"));
    case "QUARTERLY":
      return `Q${period.quarter()} ${period.year()}`;
    case "YEARLY":
      return capitalize(period.format("YYYY"));
    default:
      return " - ";
  }
};

export type FinanceCell = {
  period: string;
  isBlueprint: boolean;
  order: number;
};

export type CategoryCell = Omit<
  NonNullable<BalancePropertyQuery & BalancePortfolioQuery & ResultsPropertyQuery & ResultsPortfolioQuery>["categoryCells"][number],
  "__typename"
>;

export type PropertyCategoryCell = CategoryCell & { propertyId: string };

export const isEqual = (next: { cellKey: string; value: number }, prev?: { cellKey: string; value: number }) =>
  !!prev && next.cellKey === prev.cellKey && next.value === prev.value;

export interface ColumnHeaderConfigBase {
  type: ColumnHeaderConfigType;
  label: string;
  colSpan: number;
  even: boolean;
  key: string;
}

export interface ValueHeaderConfigBase extends ColumnHeaderConfigBase {
  groupHeader: GroupColumnHeaderConfig;
  value: ColumnHeaderConfigValue;
  type: ValueColumnHeaderType;
}

export type ColumnHeaderConfigType = ColumnHeaderConfig["type"];
export type ColumnHeaderConfigValue = ValueColumnHeaderConfig["value"];

export interface PeriodColumnHeaderConfig extends ColumnHeaderConfigBase {
  type: "period";
  period: Moment;
}

export interface PropertyColumnHeaderConfig extends ColumnHeaderConfigBase {
  type: "property";
  property: Property;
}

export interface TotalColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "total";
  value: (row: AccountsRow) => number;
}

export interface ChangeColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "change";
  value: (row: AccountsRow) => number;
}

export interface ChangePercentageColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "changePercentage";
  value: (row: AccountsRow) => number;
}

export interface BudgetColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "budget";
  value: (row: AccountsRow) => number;
}

export interface BudgetDiffColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "budgetDiff";
  value: (row: AccountsRow) => number;
}

export interface BudgetForecastColumnHeaderConfig extends ValueHeaderConfigBase {
  type: "budgetForecast";
  value: (row: AccountsRow) => number;
}

export type ValueColumnHeaderType = ValueColumnHeaderConfig["type"];

export type ValueColumnHeaderConfig =
  | TotalColumnHeaderConfig
  | ChangePercentageColumnHeaderConfig
  | ChangeColumnHeaderConfig
  | BudgetColumnHeaderConfig
  | BudgetDiffColumnHeaderConfig
  | BudgetForecastColumnHeaderConfig;

export type GroupColumnHeaderConfig = PropertyColumnHeaderConfig | PeriodColumnHeaderConfig;

export type ColumnHeaderConfig = GroupColumnHeaderConfig | ValueColumnHeaderConfig;

export const getRowValues = (row: AccountsRow, headers: ValueColumnHeaderConfig[]) =>
  headers.filter((header) => !!header.value).map((header) => header.value(row));

//#endregion

export type ExportRow = { values: number[]; name: string; level: number; sum?: boolean; expanded?: boolean };

export type ExportRows = ExportRow[];

export type AccountSpecificationsQuery = IncomeStatementAccountSpecificationsQuery &
  IncomeStatementAccountSpecificationsPortfolioQuery &
  RawBalanceAccountSpecificationsQuery &
  RawBalanceAccountSpecificationsPortfolioQuery;

export type AccountCell = {
  accountGroup: string;
  accountId: string;
  accountName: string;
  accountNumber: string;
  accountType: string;
  categoryId: string;
  period: string;
  isBlueprint: boolean;
  propertyId: string;

  totalCostWithVat: number;
  change: number;
  changePercentage: number;
};

export type AccountType = "results" | "balance";

export type AccountRow = {
  id: string;
  categoryId: string;
  accountName: string;
  columnDataObjects: ColumnDataObject[];
  type: AccountType;
  accountNumber: string;
};

export type AccountsRow = AccountRow | CategoryRow;

export type FinancialStatementsInput = {
  id: string;
  entryDateStart: Moment;
  entryDateEnd: Moment;
};

export type StatementsModalContext = {
  account: Ref<AccountRow | undefined>;
  open: Ref<boolean>;
  header: Ref<GroupColumnHeaderConfig | undefined>;
};

export type BalanceTableContext = {
  budgets: Budget[];
  categoryAccountsLoading: Ref<string[]>;
  groupHeaders: GroupColumnHeaderConfig[];
  valueHeaders: ValueColumnHeaderConfig[][];
};

export const getAccountAggregationKey = (account: { accountNumber?: string | null; accountName?: string | null }) => account.accountNumber ?? undefined;

export const BalancePreferencesInjectionKey = Symbol("balancePreferences") as InjectionKey<ComputedRef<NamespacePortfolioPropertyBalance>>;
export const BalanceTableContextInjectionKey = Symbol("balanceTableContext") as InjectionKey<ComputedRef<BalanceTableContext>>;
export const StatementsModalContextInjectionKey = Symbol("statementsModalContext") as InjectionKey<StatementsModalContext>;
export const BudgetBalanceTotalsInjectionKey = Symbol("budgetBalanceTotals") as InjectionKey<ComputedRef<Map<string, Map<string, number>>>>;

export enum BalanceAndIncomeQueryType {
  Property,
  Portfolio,
}

export type BalanceAndIncomeQueryParameters = {
  propertyId?: string | null;
  portfolioId?: string | null;
};

export const balanceDateRangeYearly = ["currentPreviousYear", "latest3", "latest5", "custom"] as const;
export const balanceDateRangeQuarterly = ["latest4", "latest8", "currentYear", "previousYear", "custom"] as const;
export const balanceDateRangeMonthly = ["currentYear", "previousYear", "currentQuarter", "previousQuarter", "yearToDate", "latest12", "custom"] as const;
export type BalanceDateRange = (typeof balanceDateRangeYearly | typeof balanceDateRangeQuarterly | typeof balanceDateRangeMonthly)[number];

export const setToPreviousPeriod = (date: Moment, periodType: MetricPeriodType) => {
  switch (periodType) {
    case MetricPeriodType.Monthly:
      date.subtract(1, "month");
      date.startOf("month");
      break;
    case MetricPeriodType.Quarterly:
      date.subtract(1, "quarter");
      date.startOf("quarter");
      break;
    case MetricPeriodType.Yearly:
      date.subtract(1, "year");
      date.startOf("year");
      break;
  }
};

export const getBudgetDateRange = (budget: Budget) => {
  return getDateRange(
    "custom",
    MetricPeriodType.Monthly,
    moment.utc(budget.validFrom).startOf("year").format("YYYY-MM-DD"),
    moment.utc(budget.validTo).endOf("year").format("YYYY-MM-DD")
  );
};

export const getDateConfigRange = (config: DateRangeConfig) =>
  getDateRange(config.dateRange, config.periodType, config.customDateStart, config.customDateEnd, config.referenceDate);

export const getDateRange = (
  balanceDateRange: BalanceDateRange,
  periodType: MetricPeriodType,
  customDateStart?: string,
  customDateEnd?: string,
  referenceDate?: string
): { from: Moment; to: Moment } => {
  const newDate = () => moment.utc(referenceDate);
  switch (balanceDateRange) {
    case "currentPreviousYear":
      return { from: fitDate(newDate(), MetricPeriodType.Yearly).subtract(1, "year"), to: fitDate(newDate(), MetricPeriodType.Yearly) };
    case "currentYear":
      return { from: fitDate(newDate(), MetricPeriodType.Yearly), to: fitDate(newDate(), MetricPeriodType.Yearly).endOf("year") };
    case "yearToDate":
      return {
        from: fitDate(newDate().startOf("year"), MetricPeriodType.Monthly),
        to: fitDate(newDate(), MetricPeriodType.Monthly).subtract(1, "month").endOf("month"),
      };
    case "previousYear":
      return {
        from: fitDate(newDate(), MetricPeriodType.Yearly).subtract(1, "year"),
        to: fitDate(newDate(), MetricPeriodType.Yearly).subtract(1, "year").endOf("year"),
      };
    case "latest3":
      return {
        from: fitDate(newDate(), MetricPeriodType.Yearly).subtract(3, "year"),
        to: fitDate(newDate(), MetricPeriodType.Yearly).subtract(1, "year"),
      };
    case "latest5":
      return {
        from: fitDate(newDate(), MetricPeriodType.Yearly).subtract(5, "year"),
        to: fitDate(newDate(), MetricPeriodType.Yearly).subtract(1, "year"),
      };
    case "currentQuarter":
      return { from: fitDate(newDate(), MetricPeriodType.Quarterly), to: fitDate(newDate(), MetricPeriodType.Quarterly).endOf("quarter") };
    case "previousQuarter":
      return {
        from: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(1, "quarter"),
        to: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(1, "quarter").endOf("quarter"),
      };
    case "latest4":
      return {
        from: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(4, "quarter"),
        to: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(1, "quarter"),
      };
    case "latest8":
      return {
        from: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(8, "quarter"),
        to: fitDate(newDate(), MetricPeriodType.Quarterly).subtract(1, "quarter"),
      };
    case "latest12":
      return {
        from: fitDate(newDate(), MetricPeriodType.Monthly).subtract(12, "month"),
        to: fitDate(newDate(), MetricPeriodType.Monthly).subtract(1, "month"),
      };
    case "custom":
      const from = moment.utc(customDateStart, "YYYY-MM-DD").startOf("month");
      const to = moment.utc(customDateEnd, "YYYY-MM-DD").endOf("month");

      switch (periodType) {
        case MetricPeriodType.Monthly:
          from.startOf("month");
          to.endOf("month");
          break;
        case MetricPeriodType.Quarterly:
          from.startOf("quarter");
          to.endOf("quarter");
          break;
        case MetricPeriodType.Yearly:
          from.startOf("year");
          to.endOf("year");
          break;
      }

      return { from, to };
    default:
      // Default fallback for unknown date range types (e.g., from newer branches)
      console.warn(`Unknown balanceDateRange: ${balanceDateRange}. Falling back to current year.`);
      return {
        from: fitDate(newDate(), MetricPeriodType.Yearly),
        to: fitDate(newDate(), MetricPeriodType.Yearly).endOf("year"),
      };
  }
};

export const getDateRangeEntries = ({ from, to }: { from: Moment; to: Moment }, periodType: MetricPeriodType) => {
  const entries: Moment[] = [];

  const current = fitDate(moment.utc(to), periodType);

  while (current.isSameOrAfter(from)) {
    entries.unshift(moment.utc(current));

    setToPreviousPeriod(current, periodType);
  }

  return entries;
};

export const extractColumnsFromCategories = (periodType: MetricPeriodType, cells: { period: string }[]) => {
  const periods = new Set<string>();

  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i];

    periods.add(cell.period);
  }

  return Array.from(periods)
    .map((p) => fitDateString(p, periodType))
    .filter((a, b, c) => c.findIndex((p) => a.valueOf() === p.valueOf()) === b)
    .sort((a, b) => b.valueOf() - a.valueOf());
};

export type BalanceColumnKey = keyof NamespacePortfolioPropertyBalance["showColumns"];

export type BalanceValueHeaderConfig = {
  id: BalanceColumnKey;
  translationKey: TranslationKey;
  description?: string;
};

export const getBalanceValueHeaders = (showColumns: NamespacePortfolioPropertyBalance["showColumns"]): BalanceValueHeaderConfig[] =>
  (
    [
      { id: "totalCostWithVat", translationKey: "PORTFOLIO_BALANCE_SETTINGS_COLUMNS_VALUE" },
      { id: "change", translationKey: "PORTFOLIO_BALANCE_SETTINGS_COLUMNS_CHANGE" },
      { id: "changePercentage", translationKey: "PORTFOLIO_BALANCE_SETTINGS_COLUMNS_CHANGE_PERCENTAGE" },
    ] satisfies BalanceValueHeaderConfig[]
  ).filter((config) => showColumns[config.id]);

export const getColumnCount = (showColumns: NamespacePortfolioPropertyBalance["showColumns"]) => {
  const { change, changePercentage, totalCostWithVat } = showColumns;

  return Number(change) + Number(changePercentage) + Number(totalCostWithVat);
};

export const periodCompleted = (period: Moment, periodType: MetricPeriodType) => {
  const currentPeriod = fitDate(moment.utc(), periodType);

  return period.isBefore(currentPeriod);
};

export const getEmptyColumnDataObject = (): ColumnDataObject => ({
  budgetValues: [],
  totalCostWithVat: 0,
  change: 0,
});

export interface GroupColumnHeaderConfigSetBase {
  type: GroupColumnHeaderConfigSetType;
  headers: Object[];
}

/**
 * Configuration for period-based column grouping.
 * Used when displaying balance data grouped by time periods (monthly, quarterly, yearly).
 */
export interface PeriodColumnHeaderConfigSet extends GroupColumnHeaderConfigSetBase {
  type: "period";
  headers: { period: Moment }[];
}

/**
 * Configuration for property-based column grouping.
 * Used when displaying balance data grouped by properties in a portfolio.
 * Enables side-by-side comparison of properties within the same time period.
 */
export interface PropertyColumnHeaderConfigSet extends GroupColumnHeaderConfigSetBase {
  type: "property";
  headers: { property: Property }[];
}

/**
 * Union type for all supported column grouping configurations.
 *
 * USAGE:
 * - Period grouping: Traditional view showing time progression for accounts
 * - Property grouping: Portfolio view showing property comparison for accounts
 *
 * The type determines how data is aggregated and displayed in the balance table.
 */
export type GroupColumnHeaderConfigSet = PropertyColumnHeaderConfigSet | PeriodColumnHeaderConfigSet;

export type GroupColumnHeaderConfigSetType = GroupColumnHeaderConfigSet["type"];

/**
 * Factory function to create header group data accessors for account rows.
 *
 * This function returns a curried function that can extract the appropriate data
 * for each header group from account rows, handling both period and property grouping.
 *
 * ARCHITECTURE:
 * The function uses the visitor pattern to handle different grouping types,
 * returning a function that maps each header to its corresponding data cells.
 *
 * @param groupColumnHeaders - Configuration defining how columns should be grouped
 * @param data - Raw account cell data to be grouped
 * @param periodType - Time period granularity for period-based grouping
 * @param budgetCells - Budget data to be associated with accounts
 * @returns Function that maps account rows to their grouped data objects
 */
export const getAccountHeaderGroups = (
  groupColumnHeaders: GroupColumnHeaderConfigSet,
  {
    data,
    periodType,
    budgetCells,
  }: {
    periodType: MetricPeriodType;
    budgetCells: AccountBudgetCell[];
    data: AccountCell[];
  }
): ((accountRow: AccountRow) => ({ dataCells: AccountCell[]; budgetCells: AccountBudgetCell[]; dataObject: ColumnDataObject } | undefined)[]) => {
  switch (groupColumnHeaders.type) {
    case "period": {
      // Group by time periods - traditional balance sheet view
      const groupedDataCells = groupBy(data, {
        period: { type: "period", getKey: (item) => fitDateString(item.period, periodType) },
        accountKey: { type: "string", getKey: getAccountAggregationKey },
      });

      const groupedBudgetCells = groupBy(budgetCells, {
        period: { type: "period", getKey: (item) => item.period },
        accountKey: { type: "string", getKey: (item) => item.accountKey },
      });

      return (row) =>
        groupColumnHeaders.headers.map((header) => {
          const accountKey = getAccountAggregationKey(row);

          if (!accountKey) return;

          const period = header.period;

          return {
            budgetCells: groupedBudgetCells.get({ period, accountKey })?.items ?? [],
            dataCells: groupedDataCells.get({ period, accountKey })?.items ?? [],
            dataObject: getEmptyColumnDataObject(),
          };
        });
    }
    case "property": {
      // Group by properties - portfolio comparison view
      const groupedDataCells = groupBy(data, {
        propertyId: { type: "string", getKey: (item) => item.propertyId },
        accountKey: { type: "string", getKey: getAccountAggregationKey },
      });

      const groupedBudgetCells = groupBy(budgetCells, {
        propertyId: { type: "string", getKey: (item) => item.propertyId },
        accountKey: { type: "string", getKey: (item) => item.accountKey },
      });

      return (row) =>
        groupColumnHeaders.headers.map((header) => {
          const accountKey = getAccountAggregationKey(row);

          if (!accountKey) return;

          const property = header.property;

          return {
            budgetCells: groupedBudgetCells.get({ propertyId: property.id, accountKey })?.items ?? [],
            dataCells: groupedDataCells.get({ propertyId: property.id, accountKey })?.items ?? [],
            dataObject: getEmptyColumnDataObject(),
          };
        });
    }
  }
};

/**
 * Factory function to create header group data accessors for category rows.
 *
 * Similar to getAccountHeaderGroups but operates on category-level data aggregation.
 * Categories are higher-level groupings that contain multiple accounts.
 *
 * @param groupColumnHeaders - Configuration defining how columns should be grouped
 * @param data - Raw category cell data with property association
 * @param periodType - Time period granularity for period-based grouping
 * @param budgetCells - Budget data to be associated with categories
 * @returns Function that maps category rows to their grouped data objects
 */
export const getCategoryHeaderGroups = (
  groupColumnHeaders: GroupColumnHeaderConfigSet,
  {
    data,
    periodType,
    budgetCells,
  }: {
    periodType: MetricPeriodType;
    budgetCells: CategoryBudgetCell[];
    data: PropertyCategoryCell[];
  }
): ((row: CategoryRow) => ({ dataCells: CategoryCell[]; budgetCells: CategoryBudgetCell[]; dataObject: ColumnDataObject } | undefined)[]) => {
  switch (groupColumnHeaders.type) {
    case "period": {
      const groupedDataCells = groupBy(data, {
        period: { type: "period", getKey: (item) => fitDateString(item.period, periodType) },
        categoryId: { type: "string", getKey: (item) => item.categoryId },
      });

      const groupedBudgetCells = groupBy(budgetCells, {
        period: { type: "period", getKey: (item) => item.period },
        categoryId: { type: "string", getKey: (item) => item.categoryId },
      });

      return (row) =>
        groupColumnHeaders.headers.map((header) => {
          const categoryId = row.id;
          const period = header.period;

          return {
            budgetCells: groupedBudgetCells.get({ period, categoryId })?.items ?? [],
            dataCells: groupedDataCells.get({ period, categoryId })?.items ?? [],
            dataObject: getEmptyColumnDataObject(),
          };
        });
    }
    case "property": {
      const groupedDataCells = groupBy(data, {
        categoryId: { type: "string", getKey: (item) => item.categoryId },
        propertyId: { type: "string", getKey: (item) => item.propertyId },
      });

      const groupedBudgetCells = groupBy(budgetCells, {
        categoryId: { type: "string", getKey: (item) => item.categoryId },
        propertyId: { type: "string", getKey: (item) => item.propertyId },
      });

      return (row) =>
        groupColumnHeaders.headers.map((header) => {
          const property = header.property;

          return {
            budgetCells: groupedBudgetCells.get({ propertyId: property.id, categoryId: row.id })?.items ?? [],
            dataCells: groupedDataCells.get({ propertyId: property.id, categoryId: row.id })?.items ?? [],
            dataObject: getEmptyColumnDataObject(),
          };
        });
    }
  }
};

export const isZeroRow = (rowValues: number[]) => rowValues.every((v) => Math.abs(v || 0) < 0.01);

export type Budget = BudgetRecordsEntity & {
  id: string;
  createdAt?: Nullable<string>;
  description?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
  name: string;
  propertyId?: Nullable<string>;
};

export type ColumnDataBudgetValue = { budgetId: string; value: number; forecast: number };

export type ColumnDataObject = {
  change: number;
  totalCostWithVat: number;
  budgetValues: ColumnDataBudgetValue[];
};

export type CategoryRow = {
  id: string;
  name: string;
  columnDataObjects: ColumnDataObject[];
  expanded: boolean;
  level: number;
  children: CategoryRow[];
  hashes: string[];
  type: AccountType;
};

export const getSetValue = <K, V>(key: K, map: Map<K, V>, create: () => V) => {
  let value = map.get(key);

  if (!value) {
    value = create();

    map.set(key, value);
  }

  return value;
};

export const createCategoryRow = (category: Category, children: CategoryRow[], level: number, type: AccountType): CategoryRow => ({
  id: category.id,
  name: category.name ?? "",
  level,
  hashes: [],
  columnDataObjects: [],
  expanded: level < 2 && !!children.length,
  children,
  type,
});

export const buildCategoryTree = <T extends { children: T[] }>(
  rootIds: string[],
  allCategories: Category[],
  create: (category: Category, children: T[], level: number) => T
): T[] => {
  const childrenGroups = groupBy(allCategories, { parentCategoryId: { type: "string", getKey: (item) => item.parentCategoryId } });

  const toBranch = (c: Category, level: number): T => {
    const children = (childrenGroups.get({ parentCategoryId: c.id })?.items ?? []).map((c) => toBranch(c, level + 1));

    return create(c, children, level);
  };

  const roots = rootIds.map((rootId) => findCategoryById(allCategories, rootId)).filter(hasValue);

  return roots.map((root) => toBranch(root, 0));
};

export const buildReactiveCategoryTree = (rootId: string, allCategories: Category[], type: AccountType) =>
  reactive(buildCategoryTree<CategoryRow>([rootId], allCategories, (c, children, level) => createCategoryRow(c, children, level, type))[0]);

const emptyBudgetValue: (budgetId: string) => ColumnDataBudgetValue = (budgetId: string) => ({ budgetId, value: 0, forecast: 0 });

/** Runs bottom up */
export const runTree = <T extends { id: string; children: T[] }>(root: T, handler: (item: T) => void, level = 0) => {
  root.children.forEach((c) => runTree(c, handler, level + 1));

  handler(root);
};

/** Runs top down. Can be stopped early by returning false */
export const runTreeDown = <T extends { id: string; children: T[] }>(root: T, handler: (item: T) => boolean, level = 0): boolean => {
  let shouldContinue = handler(root);

  if (shouldContinue) {
    for (let child of root.children) {
      shouldContinue = runTreeDown(child, handler, level + 1);

      if (!shouldContinue) return false;
    }
  }

  return shouldContinue;
};

// Pure functions for data aggregation
const aggregateChildrenTotals = (children: CategoryRow[], index: number) => {
  // For balance data with property grouping, children already contain the correct values
  // since each child was processed with the same aggregation logic
  return {
    totalCostWithVat: sum(children.map((child) => child.columnDataObjects[index].totalCostWithVat)),
    change: sum(children.map((child) => child.columnDataObjects[index].change)),
  };
};

const getLatestCell = (dataCells: (CategoryCell | AccountCell)[]) => {
  if (dataCells.length === 0) return undefined;

  // Assumes period is a string in ISO format (e.g., "2023-01-01")
  const sortedCells = dataCells.sort((a, b) => a.period.localeCompare(b.period));

  return sortedCells[sortedCells.length - 1];
};

const aggregateDataCells = (dataCells: CategoryCell[], accountType: AccountType, headerConfigType: string) => {
  // For balance data with property grouping, take latest period instead of summing
  if (accountType === "balance" && headerConfigType === "property" && dataCells.length > 0) {
    // Sort by period and take the latest
    const latestCell = getLatestCell(dataCells.filter((cell) => cell && typeof cell === "object" && !cell.isBlueprint));

    return {
      totalCostWithVat: latestCell?.totalCostWithVat ?? 0,
      change: latestCell?.change ?? 0,
      hashes: dataCells.map((cell) => cell.hash).filter(Boolean),
    };
  }

  // Default behavior: sum across periods (for results or period grouping)
  return {
    totalCostWithVat: sum(dataCells.map((cell) => cell.totalCostWithVat)),
    change: sum(dataCells.map((cell) => cell.change)),
    hashes: dataCells.map((cell) => cell.hash).filter(Boolean),
  };
};

const aggregateChildrenBudgetValues = (children: CategoryRow[], index: number): ColumnDataBudgetValue[] => {
  const budgetValues: ColumnDataBudgetValue[] = [];
  const childrenBudgetValues = children.flatMap((c) => c.columnDataObjects[index].budgetValues);

  for (let childBudgetValue of childrenBudgetValues) {
    const { budgetId, value, forecast } = childBudgetValue;

    const budgetValue = getSet(
      budgetValues,
      (bv) => bv.budgetId === budgetId,
      () => emptyBudgetValue(budgetId)
    );

    budgetValue.value += value;
    budgetValue.forecast += forecast;
  }

  return budgetValues;
};

const applyBudgetCells = (budgetValues: ColumnDataBudgetValue[], budgetCells: CategoryBudgetCell[]): void => {
  for (let budgetCell of budgetCells) {
    const { budgetId, forecast, value } = budgetCell;

    const budgetValue = pushGet(budgetValues, emptyBudgetValue(budgetId));

    budgetValue.value = value;
    budgetValue.forecast = forecast;
  }
};

/**
 * Aggregates financial data for a single category column.
 * This pure function combines children totals, direct data cells, and budget information
 * into a single ColumnDataObject.
 */
const aggregateCategoryColumn = (
  categoryRow: CategoryRow,
  index: number,
  dataCells: CategoryCell[],
  budgetCells: CategoryBudgetCell[],
  accountType: AccountType,
  headerConfigType: string
): { columnDataObject: ColumnDataObject; hashes: string[] } => {
  const columnDataObject = getEmptyColumnDataObject();

  // Aggregate children data
  const childrenTotals = aggregateChildrenTotals(categoryRow.children, index);

  // Aggregate direct data cells
  const cellAggregates = aggregateDataCells(dataCells, accountType, headerConfigType);

  // Combine totals
  columnDataObject.totalCostWithVat = childrenTotals.totalCostWithVat + cellAggregates.totalCostWithVat;
  columnDataObject.change = childrenTotals.change + cellAggregates.change;

  // Handle budget values
  columnDataObject.budgetValues = aggregateChildrenBudgetValues(categoryRow.children, index);
  applyBudgetCells(columnDataObject.budgetValues, budgetCells);

  return {
    columnDataObject,
    hashes: cellAggregates.hashes,
  };
};

/**
 * Aggregates financial data for a single account column.
 * This pure function combines direct data cells and budget information
 * into a single ColumnDataObject for accounts (which don't have children).
 */
const aggregateAccountColumn = (
  dataCells: AccountCell[],
  budgetCells: AccountBudgetCell[],
  accountType: AccountType,
  headerConfigType: string
): ColumnDataObject => {
  const columnDataObject = getEmptyColumnDataObject();

  // For balance data with property grouping, take latest period instead of summing
  if (accountType === "balance" && headerConfigType === "property" && dataCells.length > 0) {
    // Sort by period and take the latest
    const latestCell = getLatestCell(dataCells);

    columnDataObject.totalCostWithVat = latestCell?.totalCostWithVat ?? 0;
    columnDataObject.change = latestCell?.change ?? 0;
  } else {
    // Default behavior: sum across periods (for results or period grouping)
    columnDataObject.totalCostWithVat = sum(dataCells.map((cell) => cell.totalCostWithVat));
    columnDataObject.change = sum(dataCells.map((cell) => cell.change));
  }

  // Apply budget cells
  for (let budgetCell of budgetCells) {
    const { budgetId, forecast, value } = budgetCell;

    const budgetValue = pushGet(columnDataObject.budgetValues, emptyBudgetValue(budgetId));

    budgetValue.value = value;
    budgetValue.forecast = forecast;
  }

  return columnDataObject;
};

export const sumCategoryRowTree = ({
  budgetCells,
  categoryTree,
  data,
  headerConfig,
  periodType,
  accountType,
}: {
  headerConfig: GroupColumnHeaderConfigSet;
  periodType: MetricPeriodType;
  categoryTree: CategoryRow;
  data: PropertyCategoryCell[];
  budgetCells: CategoryBudgetCell[];
  accountType: AccountType;
}) => {
  const headerDataGroups = getCategoryHeaderGroups(headerConfig, { data, periodType, budgetCells });

  runTree(categoryTree, (categoryRow) => {
    const headerData = headerDataGroups(categoryRow);

    categoryRow.columnDataObjects = [];

    for (const [index, group] of headerData.entries()) {
      if (!group) continue;

      const { dataCells, budgetCells, dataObject: columnDataObject } = group;

      // Use pure function for aggregation
      const { columnDataObject: aggregatedData, hashes } = aggregateCategoryColumn(categoryRow, index, dataCells, budgetCells, accountType, headerConfig.type);

      categoryRow.columnDataObjects.push(aggregatedData);
      categoryRow.hashes.push(...hashes);
    }
  });
};

export const sumAccounts = ({
  accountRows,
  data,
  headerConfig,
  periodType,
  budgetCells,
  accountType,
}: {
  accountRows: AccountRow[];
  data: AccountCell[];
  budgetCells: AccountBudgetCell[];
  headerConfig: GroupColumnHeaderConfigSet;
  periodType: MetricPeriodType;
  accountType: AccountType;
}) => {
  const headerDataGroups = getAccountHeaderGroups(headerConfig, { data, periodType, budgetCells });

  for (let row of accountRows) {
    row.columnDataObjects = [];

    const headerData = headerDataGroups(row);

    for (const group of headerData) {
      if (!group) {
        console.warn("No group data found for row", row);
        continue;
      }

      const { dataCells: cells, budgetCells } = group;

      // Use pure function for aggregation
      const columnDataObject = aggregateAccountColumn(cells, budgetCells, accountType, headerConfig.type);
      row.columnDataObjects.push(columnDataObject);
    }
  }
};

export const accountsToRows = (accounts: Account[], type: AccountType) => {
  const accountRows: AccountRow[] = filterUniqueOn(accounts, getAccountAggregationKey).map((cell) => ({
    id: cell.id,
    accountName: cell.accountName ?? "",
    accountNumber: cell.accountNumber ?? "",
    categoryId: cell.categoryId ?? "",
    columnDataObjects: [],
    type,
  }));

  return accountRows.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
};

export const accountCellsToRows = (accountCells: AccountCell[], type: AccountType) => {
  const accountRows: AccountRow[] = [];

  accountCells.forEach((cell) => {
    if (!accountRows.find((row) => getAccountAggregationKey(row) === getAccountAggregationKey(cell))) {
      accountRows.push({
        id: cell.accountId,
        accountName: cell.accountName,
        accountNumber: cell.accountNumber,
        categoryId: cell.categoryId,
        columnDataObjects: [],
        type,
      });
    }
  });

  return accountRows.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
};

export const accountRowIsNonZero = (accountRow: AccountRow, budgets: Budget[], benchmark: BenchmarkGroupOption) =>
  accountRow.columnDataObjects.some((item) => {
    const isNonZero = (v: number) => Math.abs(v) > 0.01;

    const budgetsZero = () => {
      if (benchmark !== "budget.diff") return true;

      for (let budget of budgets) {
        const budgetValue = findBudgetValue(item.budgetValues, budget.id);

        if (budgetValue && isNonZero(budgetValue.value)) return false;
      }

      return true;
    };

    return isNonZero(item.totalCostWithVat) || !budgetsZero();
  });

export const getPeriodMetricRange = (period: Moment, periodType: MetricPeriodType) => {
  let to = moment(period);
  const from = moment(period);

  switch (periodType) {
    case "MONTHLY":
      to = to.endOf("month");
      break;
    case "QUARTERLY":
      to = to.endOf("quarter");
      break;
    case "YEARLY":
      to = to.endOf("year");
      break;
  }

  return { from, to };
};

export const getPropertySelectedBudgetIds = (preferences: NamespacePortfolioPropertyBalance, propertyId: string) =>
  preferences.propertyBudgets[propertyId] as string[] | undefined;

export const getPropertySelectedBudgets = (preferences: NamespacePortfolioPropertyBalance, propertyId: string, budgets: Budget[]) =>
  getPropertySelectedBudgetIds(preferences, propertyId)
    ?.map((id) => budgets.find((budget) => budget.id === id))
    .filter((b) => b !== undefined) ?? [];
